import { useState, useEffect } from 'react';
import { FileIcon, PlayIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { assetsApi } from '@/lib/api/assets';
import type { Asset } from '@/types/entities';
import { cn } from '@/lib/utils';
import { isImageAsset, getAssetUrl } from '@/lib/collection-field-utils';

const videoExtensions = ['mp4', 'webm', 'ogg', 'mov'];

type AssetPickerProps = {
  tenantSlug: string;
  value: number | null;
  onChange: (value: number | null) => void;
}

const PAGE_SIZE = 9;

export function AssetPicker({ tenantSlug, value, onChange }: AssetPickerProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAssets = async () => {
    if (!tenantSlug) return;

    setIsLoading(true);
    setError(null);
    try {
      const data = await assetsApi.getAllByTenantSlug(tenantSlug);
      setAssets(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load assets');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAssets();
  }, [tenantSlug]);

  const handleUpload = async (nameInput: string, file: File) => {
    setIsUploading(true);
    setError(null);
    try {
      await assetsApi.upload(tenantSlug, file, nameInput || undefined);
      // Refetch assets after successful upload
      await fetchAssets();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload asset');
      throw err;
    } finally {
      setIsUploading(false);
    }
  };

  const selectedAsset = assets.find((asset) => asset.id === value);
  const renderAssetPreview = (asset: Asset) => {
    const extension = asset.filepath.split('.').pop()?.toLowerCase() || '';
    const assetUrl = getAssetUrl(tenantSlug, asset.id);

    if (isImageAsset(asset.filepath)) {
      return (
        <img
          src={assetUrl}
          alt={asset.name}
          className="h-full w-full object-cover"
        />
      );
    }

    if (videoExtensions.includes(extension)) {
      return (
        <div className="relative h-full w-full">
          <video src={assetUrl} className="h-full w-full object-cover" />
          <div className="absolute inset-0 flex items-center justify-center bg-black/15">
            <span className="flex size-9 items-center justify-center rounded-full bg-white/80 text-foreground shadow-sm">
              <PlayIcon className="size-4 fill-current" />
            </span>
          </div>
        </div>
      );
    }

    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-muted/50 text-muted-foreground">
        <FileIcon className="size-6" />
        <span className="rounded bg-background/80 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide">
          {extension || 'file'}
        </span>
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-3 md:flex-row md:items-start">
        <div className="w-full md:w-40">
          <div className="rounded-md border bg-muted/20 overflow-hidden">
            <AspectRatio ratio={4 / 3}>
              {selectedAsset ? (
                renderAssetPreview(selectedAsset)
              ) : (
                <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
                  No asset
                </div>
              )}
            </AspectRatio>
          </div>
        </div>
        <div className="flex-1 space-y-2">
          <Input
            readOnly
            value={selectedAsset ? `${selectedAsset.name} (ID: ${selectedAsset.id})` : ''}
            placeholder="No asset selected"
          />
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" onClick={() => setIsDialogOpen(true)}>
              Select asset
            </Button>
            {value && (
              <Button type="button" variant="ghost" onClick={() => onChange(null)}>
                Clear selection
              </Button>
            )}
          </div>
          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
      </div>

      <AssetPickerDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        tenantSlug={tenantSlug}
        assets={assets}
        isLoading={isLoading}
        isUploading={isUploading}
        selectedAssetId={value}
        onSelectAsset={(assetId) => {
          onChange(assetId);
          setIsDialogOpen(false);
        }}
        onUpload={handleUpload}
        error={error}
      />
    </div>
  );
}

export interface AssetPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantSlug: string;
  assets: Asset[];
  isLoading: boolean;
  isUploading: boolean;
  selectedAssetId: number | null;
  onSelectAsset: (assetId: number) => void;
  onUpload: (nameInput: string, file: File) => Promise<void>;
  error?: string | null;
}

export function AssetPickerDialog({
  open,
  onOpenChange,
  tenantSlug,
  assets,
  isLoading,
  isUploading,
  selectedAssetId,
  onSelectAsset,
  onUpload,
  error = null,
}: AssetPickerDialogProps) {
  const [nameInput, setNameInput] = useState('');
  const [fileInput, setFileInput] = useState<File | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const sortedAssets = [...assets].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );
  const filteredAssets = sortedAssets.filter((asset) => {
    if (!normalizedSearchQuery) return true;

    return (
      asset.name.toLowerCase().includes(normalizedSearchQuery)
      || String(asset.id).includes(normalizedSearchQuery)
    );
  });
  const totalPages = Math.max(1, Math.ceil(filteredAssets.length / PAGE_SIZE));
  const pageStart = Math.max(1, currentPage - 2);
  const pageEnd = Math.min(totalPages, currentPage + 2);
  const pageNumbers = Array.from(
    { length: pageEnd - pageStart + 1 },
    (_, index) => pageStart + index
  );
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const paginatedAssets = filteredAssets.slice(startIndex, startIndex + PAGE_SIZE);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleUpload = async () => {
    if (!fileInput) return;
    await onUpload(nameInput.trim() || '', fileInput);
    // Reset form after successful upload
    setNameInput('');
    setFileInput(null);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // Reset form when closing
      setNameInput('');
      setFileInput(null);
      setSearchQuery('');
      setCurrentPage(1);
    }
    onOpenChange(newOpen);
  };

  const renderAssetPreview = (asset: Asset) => {
    const extension = asset.filepath.split('.').pop()?.toLowerCase() || '';
    const assetUrl = getAssetUrl(tenantSlug, asset.id);

    if (isImageAsset(asset.filepath)) {
      return (
        <img
          src={assetUrl}
          alt={asset.name}
          className="h-full w-full object-cover"
        />
      );
    }

    if (videoExtensions.includes(extension)) {
      return (
        <div className="relative h-full w-full">
          <video src={assetUrl} className="h-full w-full object-cover" />
          <div className="absolute inset-0 flex items-center justify-center bg-black/15">
            <span className="flex size-9 items-center justify-center rounded-full bg-white/80 text-foreground shadow-sm">
              <PlayIcon className="size-4 fill-current" />
            </span>
          </div>
        </div>
      );
    }

    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-muted/50 text-muted-foreground">
        <FileIcon className="size-6" />
        <span className="rounded bg-background/80 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide">
          {extension || 'file'}
        </span>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle>Asset Manager</DialogTitle>
          <DialogDescription>Upload new assets or select from existing ones.</DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="select" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="select">Select Asset</TabsTrigger>
            <TabsTrigger value="upload">Upload New</TabsTrigger>
          </TabsList>

          <TabsContent value="select" className="min-h-[400px]">
            {isLoading ? (
              <div className="text-sm text-muted-foreground">Loading assets...</div>
            ) : assets.length === 0 ? (
              <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
                No assets available. Upload one using the "Upload New" tab.
              </div>
            ) : filteredAssets.length === 0 ? (
              <div className="space-y-4">
                <Input
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search by asset name or ID"
                />
                <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
                  No assets match your search.
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <Input
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search by asset name or ID"
                />
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{filteredAssets.length} asset{filteredAssets.length === 1 ? '' : 's'}</span>
                  <span>Page {currentPage} of {totalPages}</span>
                </div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {paginatedAssets.map((asset) => {
                    const isActive = asset.id === selectedAssetId;
                    return (
                      <button
                        key={asset.id}
                        type="button"
                        onClick={() => onSelectAsset(asset.id)}
                        className={cn(
                          'rounded-md border p-3 text-left transition hover:border-primary/60',
                          isActive && 'border-primary bg-primary/5'
                        )}
                      >
                        <div className="overflow-hidden rounded-md border bg-muted/20">
                          <AspectRatio ratio={4 / 3}>
                            {renderAssetPreview(asset)}
                          </AspectRatio>
                        </div>
                        <div className="mt-2 space-y-1">
                          <p className="text-sm font-medium truncate">{asset.name}</p>
                          <p className="text-xs text-muted-foreground">ID: {asset.id}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  {pageNumbers.map((pageNumber) => (
                    <Button
                      key={pageNumber}
                      type="button"
                      variant={currentPage === pageNumber ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setCurrentPage(pageNumber)}
                    >
                      {pageNumber}
                    </Button>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="upload" className="min-h-[400px]">
            <div className="space-y-4 py-4">
              {error && (
                <div className="bg-destructive/10 text-destructive p-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <label htmlFor="asset-name" className="text-sm font-medium">
                  Display Name (optional)
                </label>
                <Input
                  id="asset-name"
                  placeholder="Give your asset a friendly name"
                  value={nameInput}
                  onChange={(event) => setNameInput(event.target.value)}
                  disabled={isUploading}
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="asset-file" className="text-sm font-medium">
                  Select File
                </label>
                <Input
                  id="asset-file"
                  type="file"
                  onChange={(event) => setFileInput(event.target.files?.[0] ?? null)}
                  disabled={isUploading}
                />
              </div>

              {fileInput && (
                <div className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
                  <strong>Selected file:</strong> {fileInput.name} ({(fileInput.size / 1024 / 1024).toFixed(2)} MB)
                </div>
              )}

              <p className="text-xs text-muted-foreground">
                Images, videos, documents, and other files are supported. After uploading, switch to the "Select Asset" tab to choose the asset.
              </p>

              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  onClick={handleUpload}
                  disabled={!fileInput || isUploading}
                >
                  {isUploading ? 'Uploading...' : 'Upload'}
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
